import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';
const PUBLISHER_JWT_KEY = 'publisher_jwt';

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); }`;

const Page = styled.div`
  min-height: 100vh;
  background: linear-gradient(160deg, #08080f 0%, #0d0d1c 60%, #111128 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  animation: ${fadeIn} 250ms ease;
`;

const Card = styled.div`
  background: linear-gradient(160deg, #111120 0%, #16162a 100%);
  border: 1px solid rgba(255,255,255,0.09);
  border-radius: 22px;
  padding: 48px 40px 40px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 32px 80px rgba(0,0,0,0.6);
  animation: ${slideUp} 250ms ease;
`;

const Logo = styled.div`
  font-size: 1rem;
  font-weight: 800;
  color: #ff4757;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 32px;
  text-align: center;
`;

const Title = styled.h1`
  color: white;
  font-size: 1.45rem;
  font-weight: 700;
  margin: 0 0 8px;
  text-align: center;
`;

const Sub = styled.p`
  color: rgba(255,255,255,0.38);
  font-size: 0.85rem;
  text-align: center;
  margin: 0 0 32px;
  line-height: 1.5;
`;

const EmailBadge = styled.span`
  color: #ff4757;
  font-weight: 600;
`;

const Field = styled.div`margin-bottom: 16px;`;

const Label = styled.label`
  display: block;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.45);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  background: #10101c;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  color: white;
  font-size: 0.92rem;
  padding: 12px 14px;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #ff4757; }
  &::placeholder { color: rgba(255,255,255,0.2); }
`;

const Hint = styled.p`
  font-size: 0.73rem;
  color: rgba(255,255,255,0.25);
  margin: 4px 0 0;
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 14px;
  background: #ff4757;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  margin-top: 8px;
  transition: background 0.2s, transform 0.15s;
  &:hover:not(:disabled) { background: #e0404f; transform: translateY(-1px); }
  &:disabled { opacity: 0.55; cursor: default; }
`;

const ErrorMsg = styled.p`
  color: #ff6b7a;
  font-size: 0.82rem;
  text-align: center;
  margin: 12px 0 0;
`;

const SuccessMsg = styled.p`
  color: #2ed573;
  font-size: 0.88rem;
  text-align: center;
  margin: 12px 0 0;
`;

const Spinner = styled.div`
  text-align: center;
  color: rgba(255,255,255,0.4);
  font-size: 0.9rem;
  padding: 20px 0;
`;

const PublisherInviteAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState('loading'); // loading | valid | error | done
  const [invite, setInvite] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/publisher/invite/${encodeURIComponent(token)}/validate`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
        setInvite(data);
        setStatus('valid');
      } catch (err) {
        setErrorMsg(err.message);
        setStatus('error');
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (password.length < 8) { setSubmitError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setSubmitError('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/publisher/invite/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Error ${res.status}`);
      localStorage.setItem(PUBLISHER_JWT_KEY, data.token);
      setStatus('done');
      setTimeout(() => navigate('/publisher/dashboard', { replace: true }), 1500);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Page>
      <Card>
        <Logo>vusub.ai</Logo>

        {status === 'loading' && <Spinner>Validating invite…</Spinner>}

        {status === 'error' && (
          <>
            <Title>Link unavailable</Title>
            <Sub>{errorMsg || 'This invite link is invalid, expired, or has already been used.'}</Sub>
            <Sub style={{ marginTop: 0 }}>
              Ask your VuSub account manager to generate a new invite link.
            </Sub>
          </>
        )}

        {status === 'valid' && invite && (
          <>
            <Title>Welcome to {invite.publisher_name || 'the Publisher Portal'}</Title>
            <Sub>
              You're joining as <EmailBadge>{invite.email}</EmailBadge>.<br />
              Set a password to activate your account.
            </Sub>
            <form onSubmit={handleSubmit} noValidate>
              <Field>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoFocus
                />
                <Hint>Minimum 8 characters</Hint>
              </Field>
              <Field>
                <Label>Confirm password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </Field>
              <SubmitBtn type="submit" disabled={submitting || !password || !confirmPassword}>
                {submitting ? 'Activating…' : 'Activate account'}
              </SubmitBtn>
              {submitError && <ErrorMsg>{submitError}</ErrorMsg>}
            </form>
          </>
        )}

        {status === 'done' && (
          <>
            <Title>Account activated!</Title>
            <SuccessMsg>Redirecting to your dashboard…</SuccessMsg>
          </>
        )}
      </Card>
    </Page>
  );
};

export default PublisherInviteAccept;
